import { z } from 'zod';
import { CALENDAR_PROVIDERS, ROOM_ASSET_TYPES, ROOM_AVAILABILITY_STATES, ROOM_STATUSES, SYNC_STATUSES, } from '../constants/index.js';
import { hhmmSchema, idSchema, workingHoursListSchema } from './common.js';
export const roomFeatureSchema = z.object({
    id: idSchema,
    name: z.string(),
    icon: z.string(),
    isActive: z.boolean(),
});
export const roomAssetSchema = z.object({
    id: idSchema,
    roomId: idSchema,
    type: z.enum(ROOM_ASSET_TYPES),
    url: z.string().url(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    sortOrder: z.number().int(),
});
export const roomSchema = z.object({
    id: idSchema,
    name: z.string(),
    code: z.string(),
    description: z.string().nullable(),
    capacity: z.number().int(),
    building: z.string().nullable(),
    location: z.string().nullable(),
    floor: z.string().nullable(),
    timezone: z.string(),
    color: z.string(),
    logoUrl: z.string().nullable(),
    coverImageUrl: z.string().nullable(),
    resourceEmail: z.string().nullable(),
    graphPlaceId: z.string().nullable(),
    calendarProvider: z.enum(CALENDAR_PROVIDERS),
    syncStatus: z.enum(SYNC_STATUSES),
    syncError: z.string().nullable().optional(),
    workingHours: workingHoursListSchema,
    bookingIntervalMinutes: z.number().int(),
    minDurationMinutes: z.number().int(),
    maxDurationMinutes: z.number().int(),
    maxAdvanceDays: z.number().int(),
    sortOrder: z.number().int(),
    status: z.enum(ROOM_STATUSES),
    archivedAt: z.string().nullable(),
    features: z.array(roomFeatureSchema).default([]),
    assets: z.array(roomAssetSchema).default([]),
    availability: z
        .object({
        state: z.enum(ROOM_AVAILABILITY_STATES),
        busyUntil: z.string().nullable(),
        nextReservationAt: z.string().nullable(),
    })
        .optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
const hexColor = z
    .string()
    .regex(/^#([0-9a-fA-F]{6})$/, 'Color hexadecimal de 6 dígitos, por ejemplo #253d72');
export const createRoomSchema = z.object({
    name: z.string().trim().min(2).max(120),
    code: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(/^[A-Za-z0-9._-]+$/, 'Solo letras, números, punto, guion y guion bajo'),
    description: z.string().trim().max(2000).nullish(),
    capacity: z.number().int().min(1).max(1000),
    building: z.string().trim().max(120).nullish(),
    location: z.string().trim().max(120).nullish(),
    floor: z.string().trim().max(40).nullish(),
    timezone: z.string().min(3).max(64),
    color: hexColor,
    resourceEmail: z.string().email().max(180).nullish(),
    calendarProvider: z.enum(CALENDAR_PROVIDERS).default('local'),
    workingHours: workingHoursListSchema.optional(),
    bookingIntervalMinutes: z.number().int().min(5).max(120).default(30),
    minDurationMinutes: z.number().int().min(5).max(480).default(30),
    maxDurationMinutes: z.number().int().min(15).max(1440).default(240),
    maxAdvanceDays: z.number().int().min(1).max(365).default(90),
    sortOrder: z.number().int().min(0).default(0),
    status: z.enum(ROOM_STATUSES).default('active'),
    featureIds: z.array(idSchema).max(50).default([]),
});
export const updateRoomSchema = createRoomSchema.partial();
export const reorderRoomsSchema = z.object({
    order: z.array(z.object({ id: idSchema, sortOrder: z.number().int().min(0) })).min(1),
});
export const archiveRoomSchema = z.object({
    /** Explicit acknowledgement when the room still has future reservations. */
    strategy: z.enum(['block_if_future', 'cancel_future', 'keep_until_last']).default('block_if_future'),
    reason: z.string().trim().max(500).optional(),
});
export const roomListQuerySchema = z.object({
    status: z.enum(ROOM_STATUSES).optional(),
    includeArchived: z.coerce.boolean().default(false),
    search: z.string().trim().max(120).optional(),
    minCapacity: z.coerce.number().int().min(1).optional(),
    building: z.string().trim().max(120).optional(),
    featureIds: z
        .union([z.array(idSchema), idSchema])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .optional(),
});
export const roomBlockSchema = z.object({
    id: idSchema,
    roomId: idSchema,
    reason: z.string(),
    startAtUtc: z.string(),
    endAtUtc: z.string(),
    cancelledAt: z.string().nullable(),
    createdBy: idSchema.nullable(),
    createdAt: z.string(),
});
export const createRoomBlockSchema = z
    .object({
    roomId: idSchema,
    reason: z.string().trim().min(3).max(300),
    startAtUtc: z.string().datetime(),
    endAtUtc: z.string().datetime(),
})
    .refine((v) => new Date(v.endAtUtc) > new Date(v.startAtUtc), {
    message: 'La hora final debe ser mayor que la inicial',
    path: ['endAtUtc'],
});
export const updateRoomBlockSchema = z.object({
    reason: z.string().trim().min(3).max(300).optional(),
    startAtUtc: z.string().datetime().optional(),
    endAtUtc: z.string().datetime().optional(),
});
export const validateResourceSchema = z.object({
    resourceEmail: z.string().email(),
});
export const workingHoursDefaults = (start = '08:00', end = '18:00') => [1, 2, 3, 4, 5].map((weekday) => ({ weekday, enabled: true, start, end }));
export const hhmm = hhmmSchema;
//# sourceMappingURL=room.js.map